import { Component, OnInit } from "@angular/core";
import { BroadcastService } from "@bbwt/modules/broadcasting";
import { PatientService } from "@main/app-layout/components/patient-switch/patient.service";
import { AppOnlineStateService } from "@main/pwa/app-online-state.service";
import { UserHelperService } from "@main/users/user-helper.service";
import { NamingConventionService } from "@mipro/services/naming-convention.service";
import { BehaviorSubject, combineLatestWith, concat, distinctUntilChanged, map, Observable, of, switchMap, tap } from "rxjs";
import { IncompleteDataService } from "./incomplete-data.service";
import { FormioRole } from "@main/roles/core-role";
import { CommonModule } from "@angular/common";
import { MessagesModule } from "primeng/messages";
import { RequestAccessDialogComponent } from "./request-access-dialog.component";
import { PrimeTemplate } from "primeng/api";

@Component({
    selector: "incomplete-data-warning",
    standalone: true,
    imports: [CommonModule, MessagesModule, PrimeTemplate, RequestAccessDialogComponent],
    providers: [IncompleteDataService],
    template: `
        <ng-container *ngIf="!(isPatient || isExternalUser) && (isWarningVisible$ | async)">
            <p-messages severity="warn" styleClass="m-0">
                <ng-template pTemplate>
                    <i class="pi pi-exclamation-triangle mr-2"></i>
                    <div class="flex align-items-center">
                        Data here is incomplete, there could be data from other {{ orgsLabel$ | async }} that you don't have access to.
                        <a class="cursor-pointer inline-block ml-1" (click)="requestAccessVisible = true">
                            Please click here to Request Access to this data.
                        </a>
                    </div>
                </ng-template>
            </p-messages>
        </ng-container>
        <request-access-dialog [(visible)]="requestAccessVisible" (onProceed)="accessGranted()"></request-access-dialog>
    `,
    styleUrls: ["./incomplete-data-warning.component.scss"]
})
export class IncompleteDataWarningComponent implements OnInit {
    private accessGranted$ = new BehaviorSubject(false);

    isWarningVisible$: Observable<boolean>;
    requestAccessVisible = false;
    height = 0;
    orgsLabel$ = this.namingConventions.namingConvention$.pipe(map(nc => nc.pluralOrganisationLabel));

    constructor(
        private appOnlineService: AppOnlineStateService,
        private broadcastService: BroadcastService,
        private patientService: PatientService,
        private incompleteDataService: IncompleteDataService,
        private namingConventions: NamingConventionService,
        private userHelperService: UserHelperService
    ) {}

    ngOnInit(): void {
        this.setupVisibility();
    }

    get isPatient() {
        return this.userHelperService.isPatient;
    }

    get isExternalUser() {
        return this.userHelperService.hasRole(FormioRole.FormioExternalUser);
    }

    accessGranted() {
        this.accessGranted$.next(true);
    }

    private setupVisibility() {
        const appOnline$ = concat(
            of({ isAppOnline: this.appOnlineService.isAppOnline }),
            this.broadcastService.on<{ isAppOnline: boolean }>(AppOnlineStateService.AppOnlineStateChanged)
        ).pipe(map(({ isAppOnline }) => isAppOnline));

        const hasIncompleteData$ = concat(
            of(this.patientService.currentPatientId),
            this.broadcastService.on<string>(PatientService.CurrentPatientChangedEventName)
        ).pipe(
            distinctUntilChanged(),
            switchMap(patientId => this.hasIncompleteData(patientId)),
            tap(() => this.accessGranted$.next(false))
        );

        this.isWarningVisible$ = appOnline$.pipe(
            combineLatestWith(this.accessGranted$, hasIncompleteData$),
            map(([online, accessGranted, incomplete]) => online && !accessGranted && incomplete)
        );
    }

    private async hasIncompleteData(patientId: string) {
        if (!patientId) {
            return false;
        }

        try {
            return await this.incompleteDataService.hasIncompleteData(patientId);
        } catch {
            return false;
        }
    }
}
